<template>
    <div>
        <b-tabs
            small
            pills
            card
            :navWrapperClass="'golia-pills-tabs'"
            v-model="activeTab"
            @input="$emit('tab-change', $event)"
        >
            <!-- GENERAL SETTINGS -->
            <b-tab>
                <template #title>
                    <i class="fa-solid fa-gear"></i> {{ __('options_general') }}
                </template>

                <general-settings />
            </b-tab>

            <!-- MAP SETTINGS -->
            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-map-location-dot"></i> {{ __('options_map') }}
                </template>

                <!-- VEHICLES MARKER CLUSTERING ON MAP -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_map') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            v-model="map.activeVehicleClustering"
                            id="activeVehicleClustering"
                            @change="saveVehicleClustering"
                        />
                        <label class="form-check-label" for="activeVehicleClustering">
                            {{ __('vehicles_clustering') }}
                        </label>
                    </div>
                </div>

                <!-- VEHICLES MARKER GRAPHIC OPTIONS -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_marker') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="row">
                        <div class="col-lg-7">
                            <!-- MARKER SIZE (small, medium, large) -->
                            <p
                                class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                            >
                                {{ __('size') }}
                            </p>

                            <div class="form-group d-flex align-items-center gap-2 mb-4">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-small"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[40, 54]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-small">
                                        {{ __('small') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-medium"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[52, 66]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-medium">
                                        {{ __('medium') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-large"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[60, 74]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-large">
                                        {{ __('large') }}
                                    </label>
                                </div>
                            </div>

                            <!-- MARKER FONT SIZE (small, medium, large) -->
                            <p
                                class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                            >
                                {{ __('font_size') }}
                            </p>

                            <div class="form-group d-flex align-items-center gap-2 mb-4">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-small"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'8px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-small">
                                        {{ __('small') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-medium"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'10px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-medium">
                                        {{ __('medium') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-large"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'12px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-large">
                                        {{ __('large') }}
                                    </label>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-4">
                                    <!-- MARKER BACKGROUND COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('background_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerBackgroundColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleMarkerBackgroundColor',
                                                    map.vehicleMarkerBackgroundColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- MARKER FONT COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('font_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerFontColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleMarkerFontColor',
                                                    map.vehicleMarkerFontColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- MARKER OPACITY -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('opacity') }} %
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerOpacity"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="10"
                                            class="form-control"
                                            style="font-size: 14px"
                                            @input="
                                                saveOption(
                                                    'vehicleMarkerOpacity',
                                                    map.vehicleMarkerOpacity
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- MARKER ADDITIONAL INFO -->
                            <marker-additional-data-editor />
                        </div>
                        <div class="col-lg-5" v-resized="resized">
                            <geofleet-map
                                class="geofleet-map smaller-controls rounded-3"
                                map="silica"
                                :trucks="[dummy_data.truck]"
                                :center="[
                                    dummy_data.truck.lastpoint.lat,
                                    dummy_data.truck.lastpoint.lng
                                ]"
                                :zoom="14"
                                :advanced="true"
                                :ptvToken="$env.ptv_password"
                                :extraControls="{}"
                                :noControls="true"
                                ref="settings_map"
                            >
                            </geofleet-map>
                        </div>
                    </div>
                </div>

                <!-- ADDRESS SETTINGS -->
                <h5 class="section-title mt-4 mb-2">
                    {{ __('settings_section.address.address_settings') }}
                </h5>
                <address-composition-builder
                    v-model="addressComposition"
                    :addressElements="addressElements"
                    @change="saveAddressComposition"
                />
            </b-tab>

            <!-- DRIVERS STATUS SETTINGS -->
            <b-tab lazy>
                <template #title> <i class="fa-solid fa-map-pin"></i> POI Settings </template>

                <poi-categories-settings />
            </b-tab>

            <!-- DRIVERS STATUS SETTINGS -->
            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-user-gear"></i> {{ __('driver_status') }}
                </template>

                <drivers-status-settings
                    v-model="driversStatus"
                    @saveOption="(option, value) => saveOption(option, value)"
                />
            </b-tab>

            <!-- RESET SETTINGS BUTTON -->
            <template #tabs-end>
                <b-nav-item link-classes="bg-danger text-white" @click="$emit('resetSettings')">
                    <i class="fa-solid fa-trash-can"></i> {{ __('reset_settings') }}
                </b-nav-item>
            </template>
        </b-tabs>
    </div>
</template>

<script>
import L from 'leaflet'
import { mapState, mapActions } from 'vuex'
import dummyTruck from '@/dummy_data/truck.js'
import DriversStatusSettings from './DriversStatusSettings.vue'
import AddressCompositionBuilder from './AddressCompositionBuilder.vue'
import PoiCategoriesSettings from './PoiCategoriesSettings.vue'
import MarkerAdditionalDataEditor from './MarkerAdditionalDataEditor.vue'

export default {
    name: 'GeoFleetSettings',
    components: {
        AddressCompositionBuilder,
        DriversStatusSettings,
        PoiCategoriesSettings,
        MarkerAdditionalDataEditor
    },
    props: {
        companies: {
            type: [Object, Array],
            default: () => {}
        },
        defaultTab: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            activeTab: this.defaultTab,
            loading: false,
            dummy_data: {
                truck: dummyTruck
            },
            map: {
                activeVehicleClustering: null,
                vehicleMarkerSize: [52, 66],
                vehicleMarkerFontSize: '10px',
                vehicleMarkerFontColor: null,
                vehicleMarkerBackgroundColor: null,
                vehicleMarkerOpacity: 50
            },
            analysis: {
                chart: {
                    speed: {
                        active: true
                    },
                    activity: {
                        active: true,
                        top: '79%',
                        height: '25%',
                        opposite: true
                    },
                    altitude: {
                        active: true
                    },
                    consumption: {
                        active: true,
                        format: '{value} l'
                    }
                },
                hideTimeline: false
            },
            driversStatus: {
                colors: {}
            },
            addressElements: [
                'city',
                'country',
                'country_code',
                'county',
                'hamlet',
                'municipality',
                'postcode',
                'road',
                'state',
                'suburb',
                'town',
                'village'
            ],
            addressComposition: []
        }
    },
    methods: {
        resized() {
            if (this.$refs.settings_map && this.$refs.settings_map.lmap)
                this.$refs.settings_map.lmap.invalidateSize(true)
        },
        configValue(key) {
            // Config Bus (Sprint 18, REFACTOR-030): lettura per-chiave col default
            // centralizzato nello schema, al posto dei fallback inline divergenti.
            return this.$store.getters['config/value'](key)
        },
        saveOption(option, value) {
            // Config Bus (Sprint 18, REFACTOR-030): scrittura granulare via
            // `config/commitChange` (Vue.set per-chiave) invece di `saveConfig` →
            // MERGE full-reassign → niente cascata sui consumer di config.
            // If value is an Object update its properties instead of the entire object
            if (typeof value === 'object' && Array.isArray(value) === false && value !== null) {
                this.$store.dispatch('config/commitChange', {
                    key: option,
                    // Merge col valore SALVATO (sotto-chiave raw: assente → spread di
                    // undefined = no-op, come nel legacy — niente default schema qui).
                    value: { ...this.$store.state.config.user[option], ...value }
                })
                return
            }

            this.$store.dispatch('config/commitChange', { key: option, value })
        },
        saveTableFields(fields) {
            this.saveOption('vehicleListTableFields', { fields: fields })
        },
        saveAddressComposition(value) {
            this.saveOption('addressComposition', value)
        },
        saveVehicleClustering() {
            this.$bvModal
                .msgBoxConfirm(this.__('vehicles_clustering_warning'), {
                    title: this.__('warning'),
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'success',
                    okTitle: this.__('yes'),
                    cancelTitle: this.__('no'),
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then((value) => {
                    // Yes
                    if (value) {
                        this.saveOption('activeVehicleClustering', this.map.activeVehicleClustering)
                        location.reload()
                        // No
                    } else {
                        this.saveOption('activeVehicleClustering', this.map.activeVehicleClustering)
                    }
                })
                .catch(() => {
                    // Error
                })
        },
        resetData() {
            // Sprint 19: rimosso il guard `if (this.config)` (il blob mappato era sempre
            // truthy, ramo morto) insieme al blob stesso: le letture passano tutte da
            // `configValue(key)` o dalle sotto-chiavi nude (`storedChart`).
            // Config Bus (Sprint 18, REFACTOR-030): default centralizzati nello
            // schema via `configValue(key)` — niente più fallback inline divergenti.

            // Map settings
            this.map.activeVehicleClustering = this.configValue('activeVehicleClustering')
            this.map.vehicleMarkerSize = this.configValue('vehicleMarkerSize')
            this.map.vehicleMarkerFontSize = this.configValue('vehicleMarkerFontSize')
            this.map.vehicleMarkerFontColor = this.configValue('vehicleMarkerFontColor')
            this.map.vehicleMarkerBackgroundColor = this.configValue('vehicleMarkerBackgroundColor')
            this.map.vehicleMarkerOpacity = this.configValue('vehicleMarkerOpacity')

            this.driversStatus = this.configValue('driversStatus')
            // `value(key)` applica il default schema solo se la chiave è ASSENTE
            // (hasOwnProperty): un valore presente ma null/non-array (dato legacy)
            // arriverebbe qui e farebbe throw su `.filter`, abortendo resetData()
            // PRIMA del recovery in created(). Guard difensivo a `[]`.
            this.addressComposition = (this.configValue('addressComposition') || [])
                .filter((element) => this.addressElements.includes(element))
                .map((element) => ({ id: element, label: element }))

            // For every single property of the stored chart set the relative this.analysis.chart property.
            // NB: `chart` resta lettura RAW present-check (sotto-chiave nuda) — lo schema
            // non lo centralizza (default proprio `analysis.chart`), vedi decision Sprint 18.
            if (this.storedChart)
                for (const [key, value] of Object.entries(this.storedChart)) {
                    if (this.analysis.chart[key]) this.analysis.chart[key] = value
                }
        },
        ...mapActions({
            logout: 'logout'
        })
    },
    created() {
        // For chart settings to work properly, we need to save the config in the store at the beginning
        if (!this.storedChart)
            this.$store.dispatch('config/commitChange', {
                key: 'chart',
                value: this.analysis.chart
            })

        this.resetData()
        if (!this.storedAddressComposition) {
            this.saveOption('addressComposition', ['road', 'postcode', 'city', 'country'])
        }
    },
    watch: {
        defaultTab(val) {
            this.activeTab = val
        },
        // Sprint 19: il blob serve SOLO come segnale di re-hydration — `load`/`reset`
        // RIASSEGNANO state.user (HYDRATE) → il watch scatta e resetData() ricarica i
        // campi locali (es. bottone "reset config" di SettingsPanel). Le scritture
        // granulari (Vue.set in SET_VALUE) non cambiano l'identità del blob → nessuna
        // cascata sui save. Le LETTURE non passano più dal blob.
        configBlob: function () {
            this.resetData()
        }
    },
    computed: {
        ...mapState({
            configBlob: (state) => state.config.user,
            // Sotto-chiavi nude present-check (assente → undefined, come nel legacy).
            storedChart: (state) => state.config.user.chart,
            storedAddressComposition: (state) => state.config.user.addressComposition
        })
    }
}
</script>

<style lang="scss">
input[type='color'].form-control.form-control-color {
    padding: 8px;
    border: 1px solid #d2d2d2;
    border-radius: 12px;
    height: calc(1.5em + 0.75rem);
    width: 100px;
}
</style>
